import Phaser from 'phaser';
import { itemManager } from '../../managers/ItemManager';
import { inventoryManager } from '../../managers/InventoryManager';

export default class SeedbagUI extends Phaser.GameObjects.Container {
    constructor(scene, x = 0, y = 0) {
        super(scene, x, y);
        this.scene = scene;
        
        // Debug mode state
        this.debugMode = false;
        
        // UI state
        this.isVisible = false;
        this.hoveredSlot = null;
        
        // UI dimensions
        this.viewportWidth = 256;
        this.viewportHeight = 192;
        
        // Seed selection slots (4 rows x 4 columns = 16 seeds)
        this.seedSlots = this.createSeedSlots();
        this.slotSize = 27; // Same as ToolUI
        
        this.createUI();
        this.setupInputHandlers();
        
        scene.add.existing(this);
        
        // Set high depth and fix to screen coordinates
        this.setDepth(550);
        this.setScrollFactor(0);
        
        // Start hidden
        this.setVisible(false);
    }
    
    createSeedSlots() {
        // Define all 16 seed types and their positions
        const seeds = [
            'onion', 'carrot', 'potato', 'corn', 'beet', 'cabbage', 'pea', 'beans',
            'tomato', 'wheat', 'cucumber', 'spinach', 'strawberry', 'blueberry', 'pumpkin', 'broccoli'
        ];
        
        const seedNames = [
            'Onion Seeds', 'Carrot Seeds', 'Potato Seeds', 'Corn Seeds', 'Beet Seeds', 'Cabbage Seeds', 'Pea Seeds', 'Bean Seeds',
            'Tomato Seeds', 'Wheat Seeds', 'Cucumber Seeds', 'Spinach Seeds', 'Strawberry Seeds', 'Blueberry Seeds', 'Pumpkin Seeds', 'Broccoli Seeds'
        ];
        
        const slots = [];
        const positions = [
            // Row 1
            { x: 80, y: 30 }, { x: 113, y: 30 }, { x: 146, y: 30 }, { x: 179, y: 30 },
            // Row 2
            { x: 80, y: 62 }, { x: 113, y: 62 }, { x: 146, y: 62 }, { x: 179, y: 62 },
            // Row 3
            { x: 80, y: 94 }, { x: 113, y: 94 }, { x: 146, y: 94 }, { x: 179, y: 94 },
            // Row 4
            { x: 80, y: 126 }, { x: 113, y: 126 }, { x: 146, y: 126 }, { x: 179, y: 126 }
        ];
        
        positions.forEach((pos, index) => {
            if (index < seeds.length) {
                slots.push({
                    x: pos.x-1,
                    y: pos.y-1, // Base position for hover detection
                    spriteY: pos.y-6, // Sprite position (moved up 5 pixels)
                    seed: seeds[index],
                    name: seedNames[index],
                    seedId: index + 1 // Seeds_1 through Seeds_16
                });
            }
        });
        
        return slots;
    }
    
    createUI() {
        // Create the main UI background
        this.background = this.scene.add.image(this.viewportWidth / 2, this.viewportHeight / 2, 'seedbag-ui');
        this.background.setOrigin(0.5);
        this.background.setScrollFactor(0);
        this.background.setDepth(550);
        this.background.setVisible(false);
        
        // Create hover overlay (initially invisible)
        this.hoverOverlay = this.scene.add.image(0, 0, 'tool-ui-icon-empty-hover');
        this.hoverOverlay.setOrigin(0.5, 0.5); // Explicitly set center origin for pixel-perfect alignment
        this.hoverOverlay.setVisible(false);
        this.hoverOverlay.setScrollFactor(0);
        this.hoverOverlay.setDepth(552);
        
        // Create seed icons using ItemManager's createSeedBagTexture method
        this.seedIcons = [];
        this.quantityTexts = []; // Track quantity text overlays
        
        this.seedSlots.forEach((slot, index) => {
            // Use crop spritesheet directly with frame 1 (0-indexed) for seedbag icon
            const icon = this.scene.add.image(Math.round(slot.x), Math.round(slot.spriteY || slot.y), slot.seed, 1);
            icon.setOrigin(0.5, 0.5); // Explicitly set center origin to match InventoryItem behavior
            icon.setScrollFactor(0);
            icon.setDepth(553); // Above hover overlay
            icon.setVisible(false);
            icon.setInteractive(); // Make the icon itself interactive
            
            // Store slot data on the icon
            icon.slotIndex = index;
            icon.seedData = slot;
            
            this.seedIcons.push(icon);
            
            // Create quantity text for this slot (positioned at top-right of icon)
            const quantityText = this.scene.add.text(Math.round(slot.x + 8), Math.round((slot.spriteY || slot.y) - 5), '', {
                fontSize: '8px',
                color: '#ffffff',
                stroke: '#000000',
                strokeThickness: 1,
                fontStyle: 'bold'
            });
            quantityText.setOrigin(0.5, 0.5);
            quantityText.setScrollFactor(0);
            quantityText.setDepth(554); // Above icons
            quantityText.setVisible(false);
            
            this.quantityTexts.push(quantityText);
        });
        
        // Create debug graphics
        this.debugGraphics = this.scene.add.graphics();
        this.debugGraphics.setScrollFactor(0);
        this.debugGraphics.setDepth(560);
        
        // Create interactive areas for seed slots
        this.setupSeedIconInteraction();
        
        // Create close button
        this.setupCloseButton();
        
        // Setup tooltip
        this.setupTooltip();
    }
    
    setupSeedIconInteraction() {
        // Setup hover events on the actual seed icons (like VendorScene)
        this.scene.input.on('gameobjectover', (pointer, gameObject) => {
            if (this.seedIcons.includes(gameObject) && this.isVisible) {
                this.hoveredSlot = gameObject.slotIndex;
                // Position hover overlay at the base slot position (not sprite position)
                const slot = this.seedSlots[gameObject.slotIndex];
                this.hoverOverlay.setPosition(Math.round(slot.x), Math.round(slot.y));
                this.hoverOverlay.setVisible(true);
                
                // Show tooltip
                this.updateTooltip(gameObject);
                this.tooltip.setVisible(true);
            }
        });

        this.scene.input.on('gameobjectout', (pointer, gameObject) => {
            if (this.seedIcons.includes(gameObject)) {
                this.hoveredSlot = null;
                this.hoverOverlay.setVisible(false);
                this.tooltip.setVisible(false);
            }
        });

        this.scene.input.on('gameobjectdown', (pointer, gameObject) => {
            if (this.seedIcons.includes(gameObject) && this.isVisible) {
                const slot = gameObject.seedData;
                const seedId = `Seeds_${slot.seedId}`;
                
                // Check if player owns this seed
                const playerQuantity = this.getPlayerSeedQuantity(seedId);
                
                if (playerQuantity <= 0) {
                    return;
                }
                
                if (pointer.leftButtonDown()) {
                    pointer.event.stopPropagation(); // Prevent click from bubbling to scene
                    // Set blocking flag on the scene to prevent tool actions
                    this.scene.uiInteractionBlocked = true;
                    // Safety timeout to clear the flag
                    this.scene.time.delayedCall(100, () => {
                        this.scene.uiInteractionBlocked = false;
                    });
                    this.equipSeedToSlot(seedId, 1);
                } else if (pointer.rightButtonDown()) {
                    pointer.event.preventDefault(); // Prevent browser context menu
                    pointer.event.stopPropagation(); // Prevent click from bubbling to scene
                    // Set blocking flag on the scene to prevent tool actions
                    this.scene.uiInteractionBlocked = true;
                    // Safety timeout to clear the flag
                    this.scene.time.delayedCall(100, () => {
                        this.scene.uiInteractionBlocked = false;
                    });
                    this.equipSeedToSlot(seedId, 2);
                }
            }
        });
    }
    
    setupCloseButton() {
        // Add close button (X) positioned for the seedbag UI
        this.closeButton = this.scene.add.text(208, 6, 'X', {
            fontSize: '16px',
            fill: '#ffffff',
            fontStyle: 'bold'
        }).setOrigin(0.5)
          .setInteractive()
          .setDepth(556) // Above all other elements
          .setScrollFactor(0)
          .setVisible(false);

        this.closeButton.on('pointerover', () => this.closeButton.setTint(0xdddddd));
        this.closeButton.on('pointerout', () => this.closeButton.clearTint());
        this.closeButton.on('pointerdown', () => this.closeUI());
    }
    
    setupTooltip() {
        this.tooltip = this.scene.add.container(0, 0);
        this.tooltip.setAlpha(0.8);
        this.tooltip.setDepth(1000);
        this.tooltip.setScrollFactor(0);
        
        this.tooltipBg = this.scene.add.rectangle(0, 0, 155, 40, 0x78716C)
            .setOrigin(0)
            .setStrokeStyle(2, 0xffffff);
        this.tooltip.add(this.tooltipBg);
        
        this.titleText = this.scene.add.text(3, 3, '', { 
            fontSize: '12px',
            fontStyle: 'bold',
            fill: '#ffffff',
            wordWrap: { width: 140 }
        });
        
        this.typeText = this.scene.add.text(3, 20, '', {
            fontSize: '12px',
            fontStyle: 'italic',
            fill: '#1C1917'
        });
        
        this.quantityText = this.scene.add.text(3, 35, '', {
            fontSize: '12px',
            fill: '#ffff00' // Yellow for quantity
        });
        
        this.descriptionText = this.scene.add.text(3, 50, '', {
            fontSize: '12px',
            fill: '#ffffff',
            wordWrap: { width: 140 }
        });
        
        this.tooltip.add([this.titleText, this.typeText, this.quantityText, this.descriptionText]);
        this.tooltip.setVisible(false);
    }
    
    setupInputHandlers() {
        // Y key toggles seedbag UI
        this.scene.input.keyboard.on('keydown-Y', () => {
            this.toggleVisibility();
        });
        
        // T key closes seedbag UI (when tool selection is opened)
        this.scene.input.keyboard.on('keydown-T', () => {
            if (this.isVisible) {
                this.closeUI();
            }
        });

        this.scene.input.keyboard.on('keydown-ESC', async () => {
            if (this.isVisible) {
                this.closeUI();
            }
        });
        
        // X key toggles debug mode
        this.scene.input.keyboard.on('keydown-X', () => {
            this.debugMode = !this.debugMode;
            this.updateDebugVisuals();
        });
    }
    
    toggleVisibility() {
        this.isVisible = !this.isVisible;
        
        // Update visibility of all UI elements
        this.background.setVisible(this.isVisible);
        this.seedIcons.forEach(icon => icon.setVisible(this.isVisible));
        this.quantityTexts.forEach(text => text.setVisible(this.isVisible));
        this.closeButton.setVisible(this.isVisible);
        
        // Update inventory display when showing
        if (this.isVisible) {
            this.updateInventoryDisplay();
        }
        
        // Hide hover overlay when closing
        if (!this.isVisible) {
            this.hoverOverlay.setVisible(false);
            this.hoveredSlot = null;
        }
        
        this.updateDebugVisuals();
    }
    
    closeUI() {
        if (this.isVisible) {
            this.isVisible = false;            
            // Update visibility of all UI elements
            this.background.setVisible(false);
            this.seedIcons.forEach(icon => icon.setVisible(false));
            this.quantityTexts.forEach(text => text.setVisible(false));
            this.closeButton.setVisible(false);
            
            // Hide hover overlay
            this.hoverOverlay.setVisible(false);
            this.hoveredSlot = null;
            
            this.updateDebugVisuals();
        }
    }
    
    updateDebugVisuals() {
        this.debugGraphics.clear();
        
        if (this.debugMode && this.isVisible) {
            // Draw debug rectangles over seed slots
            this.debugGraphics.lineStyle(2, 0xff6600); // Orange border
            this.debugGraphics.fillStyle(0xff6600, 0.3); // Orange fill with transparency
            
            this.seedSlots.forEach((slot, index) => {
                const rect = new Phaser.Geom.Rectangle(
                    slot.x - this.slotSize / 2,
                    slot.y - this.slotSize / 2,
                    this.slotSize,
                    this.slotSize
                );
                this.debugGraphics.strokeRectShape(rect);
                this.debugGraphics.fillRectShape(rect);
            });
            
            // Add debug text
            if (!this.debugText) {
                this.debugText = this.scene.add.text(10, 80, '', {
                    fontFamily: 'Arial',
                    fontSize: '11px',
                    color: '#ff6600'
                });
                this.debugText.setScrollFactor(0);
                this.debugText.setDepth(600);
            }
            
            const hoveredSeed = this.hoveredSlot !== null ? this.seedSlots[this.hoveredSlot] : null;
            this.debugText.setText([
                'Seedbag UI Debug Mode',
                `Visible: ${this.isVisible}`,
                `Hovered: ${hoveredSeed ? hoveredSeed.name : 'none'}`,
                'Left Click: Assign to Slot 1',
                'Right Click: Assign to Slot 2',
                `Total Seeds: ${this.seedSlots.length}`,
                'Grid: 4x4 layout'
            ]);
            this.debugText.setVisible(true);
        } else {
            if (this.debugText) {
                this.debugText.setVisible(false);
            }
        }
    }
    
    destroy() {
        // Clean up all created objects
        if (this.background) this.background.destroy();
        if (this.hoverOverlay) this.hoverOverlay.destroy();
        if (this.debugText) this.debugText.destroy();
        if (this.debugGraphics) this.debugGraphics.destroy();
        
        this.seedIcons.forEach(icon => icon.destroy());
        this.quantityTexts.forEach(text => text.destroy());
        if (this.closeButton) this.closeButton.destroy();
        // Interactive areas no longer needed - icons handle interaction directly
        // Note: Scene-level input events will be cleaned up automatically when scene/icons are destroyed
        
        super.destroy();
    }
    
    // Update display based on player inventory
    updateInventoryDisplay() {
        this.seedSlots.forEach((slot, index) => {
            const seedId = `Seeds_${slot.seedId}`;
            const quantity = this.getPlayerSeedQuantity(seedId);
            
            const icon = this.seedIcons[index];
            const quantityText = this.quantityTexts[index];
            
            if (quantity > 0) {
                // Player owns this seed - show normally
                icon.clearTint();
                quantityText.setText(quantity.toString());
                // Only show quantity text if the SeedbagUI is currently visible
                quantityText.setVisible(this.isVisible);
            } else {
                // Player doesn't own this seed - desaturate
                icon.setTint(0x666666);
                quantityText.setVisible(false);
            }
        });
    }
    
    // Get player's quantity of a specific seed
    getPlayerSeedQuantity(seedId) {
        const inventory = inventoryManager.getInventory();
        const seedItem = inventory.find(item => item.itemCode === seedId);
        return seedItem ? seedItem.quantity : 0;
    }
    
    // Public method for external updates (called when seeds are consumed)
    updateSeedQuantities() {
        this.updateInventoryDisplay();
    }
    
    // Equip seed to tool slot
    async equipSeedToSlot(seedId, slotNumber) {
        try {
            // Update the ToolUI display
            if (this.scene.toolUI) {
                this.scene.toolUI.updateEquippedTool(slotNumber, seedId);
            }
            
            // Make API call to save equipment state
            await this.saveEquipmentState(slotNumber, seedId);
            
        } catch (error) {
            console.error('Failed to equip seed:', error);
        }
    }
    
    // Save equipment state to backend
    async saveEquipmentState(slotNumber, itemCode) {
        // TODO: Implement API call to update user_options table
        // This will be implemented when backend integration is ready
    }
    
    // Update tooltip content
    updateTooltip(gameObject) {
        const slot = gameObject.seedData;
        const seedId = `Seeds_${slot.seedId}`;
        const quantity = this.getPlayerSeedQuantity(seedId);
        
        this.titleText.setText(slot.name);
        this.titleText.setColor('#ffffff');
        this.typeText.setText('Seeds');
        
        const quantityText = quantity > 0 ? 
            `Owned: ${quantity}` : 
            'Not owned';
        this.quantityText.setText(quantityText);
        
        // Get seed description from items.json if available
        const seedData = itemManager.getItemData(seedId);
        const description = seedData ? seedData.description : 'Plant these seeds to grow crops.';
        this.descriptionText.setText(description);
        this.descriptionText.setFontStyle("italic");

        // Calculate tooltip dimensions and position
        let totalHeight = 3 + 
            this.titleText.height + 
            this.typeText.height +
            this.quantityText.height +
            this.descriptionText.height + 
            12;
        
        const largestWidth = Math.max(
            this.titleText.width, 
            this.typeText.width, 
            this.quantityText.width,
            this.descriptionText.width
        );

        this.tooltipBg.setSize(largestWidth + 6, totalHeight);

        const UI_WIDTH = 256;
        const UI_HEIGHT = 192;
        const TOOLTIP_MARGIN = 5;

        let tooltipX = gameObject.x + 16;
        let tooltipY = gameObject.y + 16;

        // Adjust horizontal position
        if (tooltipX + largestWidth > UI_WIDTH) {
            tooltipX = gameObject.x - largestWidth - TOOLTIP_MARGIN;
        }

        if (tooltipX < 0) {
            tooltipX = TOOLTIP_MARGIN;
        }

        // Adjust vertical position
        if (tooltipY + totalHeight > UI_HEIGHT) {
            tooltipY = gameObject.y - totalHeight - TOOLTIP_MARGIN;
        }

        if (tooltipY < 0) {
            tooltipY = TOOLTIP_MARGIN;
        }

        this.tooltip.setPosition(tooltipX, tooltipY);
    }
} 